<template>
  <div class="company-details">
    <CreateContactModal
      v-if="showContactModal"
      :companyId="company.CompanyId"
      @closeModal="onCloseContactModal"
    />
    <header>
      <span class="title">{{ props.company.CompanyName }}</span>
      <span class="company-details__close" @click="onClose">
        <font-awesome-icon icon="times" />
      </span>
    </header>
    <form class="new-comment" @submit.prevent="onSubmit">
      <header v-if="!interactionToUpdate">Add New Interaction</header>
      <header v-else>
        Update Interaction created on
        {{ moment(interactionToUpdate.ActivityDate).format("MM/DD/YYYY") }}
        <font-awesome-icon
          icon="undo"
          class="company-details__cancel-update"
          @click="resetFormState"
        />
      </header>
      <div>
        <div class="form-row">
          <select
            v-model="selectedTypeText"
            :disabled="disableInteractionsModification"
            required
          >
            <option class="mode-option" disabled value="" selected hidden>
              Mode
            </option>
            <option
              class="mode-option"
              v-for="(type, i) in InteractionTypes"
              :key="i"
            >
              {{ type.text }}
            </option>
          </select>
          <DatePicker
            placeholder="Select Date"
            v-model="selectedDate"
            :max="new Date()"
            :disabled="disableInteractionsModification"
            required
          />
        </div>
        <div class="form-row">
          <div class="search">
            <SearchIcon />
            <Loader
              v-if="loadingContacts"
              class="company-details-loader"
              type="pulsing"
              data-cy="contacts-loader"
            />
            <DropDownList
              v-else
              id="contact-person"
              name="contact-person"
              class="contact-dropdown"
              :dataItems="contacts"
              v-model="selectedContact"
              text-field="fullName"
              data-item-key="Contact.Id"
              placeholder="Search typing to select a Contact"
              :disabled="disableInteractionsModification"
              required
            />
          </div>
          <font-awesome-icon
            class="create-contact-icon"
            icon="user-plus"
            @click="showContactModal = true"
          />
        </div>
        <div class="form-row">
          <textarea
            placeholder="Comment"
            v-model="interactionComment"
            :disabled="disableInteractionsModification"
          ></textarea>
        </div>
        <div class="form-row">
          <LoadingButton
            v-if="!disableInteractionsModification"
            type="submit"
            theme-color="primary"
            :loading="submitting"
            >{{
              interactionToUpdate ? "Update Interaction" : "Submit Interaction"
            }}</LoadingButton
          >
        </div>
      </div>
    </form>
    <div class="company-details__comments-list">
      <Loader
        v-show="loadingInteractions"
        class="company-details-loader"
        size="large"
        type="pulsing"
        data-cy="interactions-loader"
      />
      <section v-if="latestInteraction" class="comments-section">
        <header>Last Interaction</header>
        <CompanyCommentItem
          v-if="latestInteraction"
          :comment="latestInteraction"
          @click="selectInteraction(latestInteraction)"
        />
      </section>
      <section v-if="lastMonthInteractions.length" class="comments-section">
        <header>Last 30 days</header>
        <div class="comments-list">
          <CompanyCommentItem
            v-for="(interaction, i) in lastMonthInteractions"
            :key="i"
            :comment="interaction"
            @click="selectInteraction(interaction)"
          />
        </div>
      </section>
      <section v-if="lastYearInteractions.length" class="comments-section">
        <header>Earlier this year</header>
        <div class="comments-list">
          <CompanyCommentItem
            v-for="(interaction, i) in lastYearInteractions"
            :key="i"
            :comment="interaction"
            @click="selectInteraction(interaction)"
          />
        </div>
      </section>
      <section v-if="olderInteractions.length" class="comments-section">
        <header>Older</header>
        <div class="comments-list">
          <CompanyCommentItem
            v-for="(interaction, i) in olderInteractions"
            :key="i"
            :comment="interaction"
            @click="selectInteraction(interaction)"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from "vue";
import SearchIcon from "@/icons/CrmSearchIcon.vue";
import { DatePicker } from "@progress/kendo-vue-dateinputs";
import moment from "moment";
import CompanyCommentItem from "./CompanyCommentItem.vue";
import type { MiniCrmInteraction } from "@/Domain/MiniCrmInteraction";
import { useMainStore } from "@/stores/useMainStore";
import type { MiniCrmCompany } from "@/Domain/MiniCrmCompany";
import { InteractionTypes } from "@/Domain/EnumMiniCrmInteractionType";
import { EnumMiniCrmFollowUpType } from "@/Domain/EnumMiniCrmFollowUpType";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import type { CompanycontactsTb } from "@/Domain/CompanycontactsTb";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { Loader } from "@progress/kendo-vue-indicators";
import { useMiniCrmStore } from "@/stores/useMiniCrmStore";
import CreateContactModal from "./CreateContactModal.vue";
import { UserRoles } from "@/Domain/UserRoles";

interface ContactExtended extends CompanycontactsTb {
  fullName: string;
}

const main = useMainStore();
const notificationStore = useNotificationStore();
const crmStore = useMiniCrmStore();

const props = defineProps<{
  company: MiniCrmCompany;
}>();

const emit = defineEmits(["close"]);

// Defaults to today
const selectedDate = ref(new Date());

const contacts = ref<ContactExtended[]>([]);
const selectedContact = ref<ContactExtended | null>(null);
const loadingContacts = ref<boolean>(false);

const interactions = ref<MiniCrmInteraction[]>([]);
const interactionToUpdate = ref<MiniCrmInteraction | null>(null);
const interactionComment = ref<string>("");
const loadingInteractions = ref<boolean>(false);
const showContactModal = ref<boolean>(false);

const submitting = ref<boolean>(false);

const selectedTypeText = ref<string>("");

const latestInteraction = computed(() => {
  return [...interactions.value].sort(
    (a, b) =>
      new Date(b.ActivityDate).getTime() - new Date(a.ActivityDate).getTime()
  )[0];
});

const lastMonthInteractions = computed(() => {
  return interactions.value.filter(
    (x) =>
      moment(x.ActivityDate).isAfter(moment().subtract(1, "month")) &&
      moment(x.ActivityDate).isBefore(moment()) &&
      x !== latestInteraction.value
  );
});

const lastYearInteractions = computed(() => {
  return interactions.value.filter(
    (x) =>
      moment(x.ActivityDate).isAfter(moment().subtract(1, "year")) &&
      moment(x.ActivityDate).isBefore(moment().subtract(1, "month")) &&
      x !== latestInteraction.value
  );
});

const olderInteractions = computed(() => {
  return interactions.value.filter(
    (x) =>
      moment(x.ActivityDate).isBefore(moment().subtract(1, "year")) &&
      x !== latestInteraction.value
  );
});

const disableInteractionsModification = computed(() => {
  return (
    crmStore.emulatedPersonJobId !== 0 &&
    main.isInRole(UserRoles.ROLE_GROUP_LEADER) &&
    !main.isInAnyRole([
      UserRoles.ROLE_MIS,
      UserRoles.ROLE_IN_HOUSE_MIS_MANAGER,
      UserRoles.ROLE_MANAGEMENT,
    ])
  );
});

const getInteractionsAsync = async function () {
  loadingInteractions.value = true;

  const getInteractionsPromise = api.get("GetCrmInteractions", {
    userLogin: main.userLogin,
    emulatedPersonJobId: crmStore.emulatedPersonJobId,
    companyId: props.company.CompanyId,
    take: 999999,
    skip: 0,
  });

  await handleResponsePromiseAsync(
    getInteractionsPromise,
    (data) => {
      interactions.value = data.ListItems;
    },
    "CompanyDetails.vue",
    "getInteractionsAsync",
    (error) => {}
  );

  loadingInteractions.value = false;
};

const onClose = () => emit("close");

const onCloseContactModal = (contactCreated: boolean) => {
  showContactModal.value = false;
  if (contactCreated) getContactsAsync();
};

const saveInteractionAsync = async function () {
  const interactionType = InteractionTypes.find(
    (x) => x.text === selectedTypeText.value
  );

  if (!interactionType) {
    return;
  }

  if (selectedContact.value == null) {
    return;
  }

  const requestParams = {
    userLogin: main.userLogin,
    emulatedPersonJobId: crmStore.emulatedPersonJobId,
    contactId: selectedContact.value.Contact.Id,
    activityDate: selectedDate.value.toLocaleDateString("en-US"),
    interactionType: interactionType.value,
    // Fallback to Reached type since user cannot select this
    result: EnumMiniCrmFollowUpType.Reached,
    comment: interactionComment.value,
  };

  const errorMessage = `Error happened while trying to save interaction for company ${props.company.CompanyName}. Please try again later.`;

  let apiPromise = null;
  // Update
  if (interactionToUpdate.value) {
    apiPromise = api.post("UpdateCrmInteraction", {
      ...requestParams,
      interactionId: interactionToUpdate.value.Id,
    });
  }
  // Create
  else {
    apiPromise = api.post("CreateCrmInteraction", {
      ...requestParams,
      companyId: props.company.CompanyId,
    });
    console.info(
      "CompanyDetails, saveInteractionAsync, create",
      requestParams,
      props.company.CompanyId
    );
  }

  return handleResponsePromiseAsync(
    apiPromise,
    (data) => {
      if (!data) {
        notificationStore.notify({
          message: errorMessage,
          type: NotificationTypeEnum.error,
          duration: 5000,
        });
        return;
      }
      notificationStore.notify({
        message: "Interaction saved successfully.",
        type: NotificationTypeEnum.success,
        duration: 5000,
      });
      resetFormState();
      getInteractionsAsync();
    },
    "CompanyDetails.vue",
    "saveInteractionAsync",
    (error) => {
      notificationStore.notify({
        message: errorMessage,
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
    }
  );
};

const getContactsAsync = async function () {
  loadingContacts.value = true;

  const contactsPromise = api.get("GetCompanyContacts", {
    userLogin: main.userLogin,
    companyLogin: props.company.CompanyId.toString(),
    fields: "",
    operators: "",
    values: "",
  });

  await handleResponsePromiseAsync(
    contactsPromise,
    (data) => {
      contacts.value = data.sort((a, b) =>
        a.Contact.Email.localeCompare(b.Contact.Email)
      ) as ContactExtended[];

      contacts.value.forEach((x) => {
        x.fullName = `${x.Contact.Lastname}, ${x.Contact.Firstname}`;
      });
    },
    "CompanyDetails.vue",
    "getContactsAsync"
  );

  loadingContacts.value = false;
};

const onSubmit = async () => {
  try {
    submitting.value = true;
    await saveInteractionAsync();
  } catch (error) {
    console.error("MiniCRM, CompanyDetails, onSubmit error: ", error);
  } finally {
    submitting.value = false;
  }
};

const selectInteraction = (interaction: MiniCrmInteraction) => {
  interactionToUpdate.value = interaction;
  selectedTypeText.value = InteractionTypes.find(
    (x) => x.value === interaction.InteractionType
  )?.text!;
  selectedDate.value = new Date(interaction.ActivityDate);
  interactionComment.value = interaction.Comment;
  selectedContact.value =
    contacts.value.find((x) => x.Contact.Id === interaction.VueContactId) ??
    null;
};

const resetFormState = () => {
  interactionToUpdate.value = null;
  selectedTypeText.value = "";
  selectedDate.value = new Date();
  interactionComment.value = "";
  selectedContact.value = null;
};

watch(
  () => props.company,
  async () => {
    contacts.value = [];
    interactions.value = [];
    resetFormState();

    getInteractionsAsync();
    getContactsAsync();
  }
);

onBeforeMount(async () => {
  getInteractionsAsync();
  getContactsAsync();
});
</script>

<style lang="scss" scoped>
.company-details {
  border-left: 1px solid #eeeff0;
  background: white;
  overflow: auto;

  input,
  select,
  textarea {
    border: 1px solid #ddd;
    padding: 8px 12px;
    height: auto;
    min-height: 38px;
  }

  > header {
    background: #fbfbfb;
    padding: 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .title {
      font-size: 16px;
      color: #1f262e;
      font-weight: 700;
      line-height: 1;
    }
  }

  &__close {
    cursor: pointer;
    font-size: 18px;
    padding: 10px;

    &:hover {
      color: #444;
    }
  }

  &__cancel-update {
    cursor: pointer;
    font-size: 18px;
    padding: 10px;
    margin-left: 10px;

    &:hover {
      color: #444;
    }
  }

  .new-comment {
    background: #fbfbfb;
    padding: 0 24px 24px;

    > header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      font-size: 14px;
      font-weight: 500;
    }

    .date > div {
      display: flex;
      gap: 8px;
      align-items: center;

      .trigger {
        cursor: pointer;
        color: #325ea3;
      }

      .selected-date {
        color: #68707a;
      }

      input {
        width: 100px;
      }
    }
  }

  .form-row {
    display: flex;
    gap: 8px;
  }

  .search {
    background: white;
    flex: 1;
    border: 1px solid #ddd;
    border-radius: 4px;
    display: flex;
    padding: 3px 8px 2px;
    align-items: center;
    min-height: 41px;

    .icon {
      margin-right: 5px;
      font-size: 0;
    }

    input {
      flex: 1;
      border: none;
      margin: 0;
    }
  }

  textarea {
    min-height: 80px;
  }

  button {
    background: #325ea3;
    height: 44px;
    cursor: pointer;
    padding: 8px 12px;
    display: block;
    color: white;
    border-radius: 4px;
    border: none;
    width: 100%;
    font-size: 16px;

    &:hover {
      background: #275182;
    }
  }

  &__comments-list {
    padding: 0px 8px 12px;
    background: #fff;

    .comments-section {
      padding-top: 30px;
      border-left: 1px solid #eeeff0;
      padding-left: 10px;
      margin-left: 8px;

      > header {
        line-height: 1;
        color: #9fa4aa;
        font-size: 14px;
        margin-bottom: 10px;
        margin-left: 8px;
      }
    }
  }

  .contact-dropdown {
    border: 0;
    background: unset;
    box-shadow: unset;
  }

  .company-details-loader {
    margin: 0 auto;
    display: flex;
    justify-content: center;
  }

  .create-contact-icon {
    cursor: pointer;
    color: #325ea3;

    &:hover {
      color: #444;
    }
  }
}
</style>
